import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {HttpErrorResponse} from '@angular/common/http';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCircleInfo, faCircleXmark, faTriangleExclamation} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicPopupComponent,
  BicTextareaComponent,
  BicToastService,
} from '@buildinginformationcloud/bic-ui-components';

import {switchMap} from 'rxjs/operators';

import {
  CatalogFinding,
  CatalogInstallation,
  CatalogItemType,
  CatalogService,
  CatalogSubmission,
  catalogSubmissionStatusLabel,
  CatalogSubmitTarget,
  CatalogTargets,
} from '../../../../services/catalog.service';
import {AdminBadgeComponent} from '../../../util/design/admin-badge/admin-badge.component';
import {CatalogTargetsPickerComponent} from '../catalog-targets-picker/catalog-targets-picker.component';
import {serverErrorMessage} from '../server-error-message';

/** Länge der Spalte {@code catalog_submission.changelog}. */
const CHANGELOG_MAX_LENGTH = 2000;

const ITEM_TYPE_LABEL: Record<CatalogItemType, string> = {
  KI: 'KI-myCheck',
  IDS: 'IDS-myCheck',
  HYBRID: 'Hybrid-myCheck',
  WORKFLOW: 'Prüflauf',
};

/**
 * Erfolgstext. Bei einem enthaltenen myCheck entsteht je Prüflauf eine neue Revision – die Anzahl
 * nennt, wie viele Prüfläufe aktualisiert wurden.
 */
function successMessage(installations: CatalogInstallation[], component: boolean): string {
  const live = ' Nach Live übernimmt das Admin Center.';
  if (component) {
    return installations.length === 1
      ? `In Beta bereitgestellt – der zugehörige Prüflauf ist aktualisiert.${live}`
      : `In Beta bereitgestellt – ${installations.length} Prüfläufe sind aktualisiert.${live}`;
  }
  const revision = installations[0]?.version;
  return `In Beta bereitgestellt${revision ? ` (Revision ${revision})` : ''}.${live}`;
}

/**
 * Zentral bereitstellen eines myChecks oder Prüflaufs – wirkt in dieser Stage (beta) sofort.
 *
 * <p>Es entsteht bzw. aktualisiert sich eine projektlose, schreibgeschützte Kopie, die die gewählte
 * Zielgruppe sofort sieht. Nach Live kommt sie nur über das Admin Center, das dort auch die
 * Live-Zielgruppe festlegt (Organisationen und Nutzer haben je Stage andere IDs).
 *
 * <p>Die Zielgruppe ist beim ersten Bereitstellen Pflicht; danach bleibt sie, solange „Zielgruppe
 * ändern" nicht gewählt ist. Ein enthaltener myCheck aktualisiert seine Prüfläufe – deren
 * Zielgruppe bleibt, wie sie ist.
 */
@Component({
  selector: 'app-catalog-submission-prompt',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    FaIconComponent,
    BicPopupComponent,
    BicButtonComponent,
    BicBadgeComponent,
    BicTextareaComponent,
    AdminBadgeComponent,
    CatalogTargetsPickerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './catalog-submission-prompt.component.html',
})
export class CatalogSubmissionPromptComponent implements OnInit {
  @Input({required: true}) itemType!: CatalogItemType;
  /** Regelset-ID (myCheck) bzw. Definitions-ID (Prüflauf). */
  @Input({required: true}) sourceId!: string;
  @Input({required: true}) itemName!: string;
  /** Projekt, aus dem eingereicht wird – das Backend löst darüber projektgebundene Verweise auf. */
  @Input() projectId?: number;
  /**
   * Einreichung eines Admin-Entwurfs (zentrales Regelset oder Prüflauf-Entwurf) als neue Version
   * eines bestehenden Katalog-Eintrags – nur andere Texte, derselbe Ablauf.
   */
  @Input() newVersion = false;

  @Output() closed = new EventEmitter<void>();
  /**
   * Die Protokollzeilen dieser Quelle nach dem Bereitstellen (für das Karten-Badge) – bei einem
   * enthaltenen myCheck mehrere, eine je Prüflauf.
   */
  @Output() submitted = new EventEmitter<CatalogSubmission[]>();

  protected readonly faSpinnerThird = faSpinnerThird;
  protected readonly faTriangleExclamation = faTriangleExclamation;
  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly changelogMaxLength = CHANGELOG_MAX_LENGTH;
  protected readonly statusLabel = catalogSubmissionStatusLabel;

  protected stage = '';
  protected changelog = '';

  protected submissions: CatalogSubmission[] = [];
  protected submissionsLoading = true;
  protected submissionsFailed = false;

  protected findings: CatalogFinding[] = [];
  protected submitsAs: CatalogSubmitTarget[] = [];
  protected preflightLoading = true;
  protected preflightFailed = false;

  protected submitting = false;

  /** Gewählte Zielgruppe; {@code null} solange leer. */
  protected targets: CatalogTargets | null = null;
  /** Bei einer bestehenden Bereitstellung: Zielgruppe neu setzen statt behalten. */
  protected changeTargets = false;

  private catalogService = inject(CatalogService);
  private toastService = inject(BicToastService);

  protected get typeLabel(): string {
    return ITEM_TYPE_LABEL[this.itemType];
  }

  protected get blockers(): CatalogFinding[] {
    return this.findings.filter(finding => finding.severity === 'BLOCKER');
  }

  protected get warnings(): CatalogFinding[] {
    return this.findings.filter(finding => finding.severity === 'WARNING');
  }

  /**
   * Prüfläufe, als deren neue Version ein myCheck eingereicht wird: gesetzt, wenn der myCheck nur
   * als Teil zentraler Prüfläufe im Katalog steht (Freigabe-Einheit ist dann der Prüflauf).
   */
  protected get parentWorkflows(): CatalogSubmitTarget[] {
    if (this.itemType === 'WORKFLOW') return [];
    return this.submitsAs.filter(target => target.itemType === 'WORKFLOW');
  }

  /**
   * Einreichen erst, wenn die Vorprüfung durch ist und keinen BLOCKER meldet. Ist sie
   * fehlgeschlagen, bleibt der Button gesperrt – ohne Ergebnis wissen wir nicht, ob es Blocker gibt.
   */
  protected get canSubmit(): boolean {
    return !this.submitting
      && !this.preflightLoading
      && !this.preflightFailed
      && this.blockers.length === 0
      && this.changelog.trim().length > 0
      && (!this.targetsEditable || this.targets !== null);
  }

  /** Schon einmal bereitgestellt (bestehender Katalog-Eintrag) – dann bleibt die Zielgruppe standardmäßig. */
  protected get alreadyProvided(): boolean {
    return this.newVersion || this.submitsAs.some(target => target.catalogKey != null);
  }

  /** Zielgruppe wird in diesem Dialog gesetzt: beim ersten Mal immer, sonst nur auf Wunsch. */
  protected get targetsEditable(): boolean {
    if (this.parentWorkflows.length > 0) return false;
    return !this.alreadyProvided || this.changeTargets;
  }

  ngOnInit(): void {
    this.catalogService.getConfig().subscribe({
      next: config => (this.stage = config.stage),
      error: err => this.toastService.error(
        serverErrorMessage(err, 'Katalog-Konfiguration konnte nicht geladen werden.')),
    });
    this.loadSubmissions();
    this.runPreflight();
  }

  protected runPreflight(): void {
    this.preflightLoading = true;
    this.preflightFailed = false;
    this.catalogService.preflight(this.itemType, this.sourceId, this.projectId).subscribe({
      next: result => {
        this.findings = result.findings ?? [];
        this.submitsAs = result.submitsAs ?? [];
        this.preflightLoading = false;
      },
      error: err => {
        this.findings = [];
        this.submitsAs = [];
        this.preflightLoading = false;
        this.preflightFailed = true;
        this.toastService.error(serverErrorMessage(err, 'Vorprüfung fehlgeschlagen.'));
      },
    });
  }

  protected onChangelogChange(value: string): void {
    this.changelog = value ?? '';
  }

  protected onSubmit(): void {
    if (!this.canSubmit) return;
    this.submitting = true;
    const component = this.parentWorkflows.length > 0;
    let installed: CatalogInstallation[] = [];
    this.catalogService.provide({
      itemType: this.itemType,
      sourceId: this.sourceId,
      projectId: this.projectId,
      changelog: this.changelog.trim(),
      targets: this.targetsEditable ? this.targets : null,
    }).pipe(
      switchMap(installations => {
        installed = installations;
        return this.catalogService.listSubmissions({itemType: this.itemType, sourceId: this.sourceId});
      }),
    ).subscribe({
      next: submissions => {
        this.submitting = false;
        this.toastService.success(successMessage(installed, component));
        this.submitted.emit(submissions);
      },
      error: (err: HttpErrorResponse) => {
        this.submitting = false;
        // 422: Blocker, die erst beim Einreichen auffallen (z. B. Kontextdateien > 25 MB) – im
        // Dialog anzeigen statt nur als Toast, damit klar ist, was zu beheben ist.
        const findings = (err?.error as { findings?: CatalogFinding[] } | null)?.findings;
        if (err?.status === 422 && Array.isArray(findings)) {
          this.findings = findings;
        }
        if (installed.length) {
          // Bereitgestellt ist es; nur das Nachladen des Protokolls ist gescheitert.
          this.toastService.success(successMessage(installed, component));
          this.submitted.emit([]);
          return;
        }
        this.toastService.error(serverErrorMessage(err, 'Bereitstellen fehlgeschlagen.'));
      },
    });
  }

  protected onClose(): void {
    if (this.submitting) return;
    this.closed.emit();
  }

  private loadSubmissions(): void {
    this.submissionsLoading = true;
    this.submissionsFailed = false;
    this.catalogService.listSubmissions({itemType: this.itemType, sourceId: this.sourceId}).subscribe({
      next: submissions => {
        this.submissions = [...submissions].sort(
          (a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
        this.submissionsLoading = false;
      },
      error: err => {
        this.submissionsLoading = false;
        this.submissionsFailed = true;
        this.toastService.error(serverErrorMessage(err, 'Bisherige Bereitstellungen konnten nicht geladen werden.'));
      },
    });
  }
}
